import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { ApVendorsMapToExemptVendorResponseDto } from 'src/app/shared/vive-api/vive-api-models';

import { ApVendor } from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ApVendorService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getApVendors(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/apVendor/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/ap/apVendor/many`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  saveApVendor(ApVendor: ApVendor, query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/apVendor/one`,
              ApVendor,
              { params: query }
            ),
          Admin: () =>
            this.http.post(`/api/v2/admin/ap/apVendor/one`, ApVendor, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  saveApVendors(ApVendors: ApVendor[], query = {}): Observable<ApVendor[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(`/api/v2/admin/ap/apVendor/many`, ApVendors, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  inviteVendor(ApVendor: ApVendor, query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/apVendor/invite`,
              ApVendor,
              { params: query }
            ),
          Admin: () =>
            this.http.post(`/api/v2/admin/ap/apVendor/invite`, ApVendor, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getApVendorCount(query: {} = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/apVendor/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/ap/apVendor/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getCountOfApVendorsLinkedToVendor(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/vendor/getCountOfApVendorsLinkedToVendor`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  linkVendor(
    ApVendor: ApVendor,
    query: { [key: string]: any } = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.put(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/vendor/${query['id']}`,
              ApVendor,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  unLinkVendor(
    ApVendor: ApVendor,
    query: { [key: string]: any } = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.put(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/vendor/${query['id']}`,
              ApVendor,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  mapApVendorsToExemptVendors({
    apConfigId,
    legalNames,
    apVendorKeys
  }: {
    apConfigId: number;
    legalNames?: string[];
    apVendorKeys?: string[];
  }): Observable<ApVendorsMapToExemptVendorResponseDto> {
    return this.http.get<ApVendorsMapToExemptVendorResponseDto>(
      `/api/v3/ap-vendors/ap-configs/${apConfigId}/map-to-exempt-vendor`,
      {
        params: {
          ...(legalNames &&
            legalNames.length > 0 && { legalNames: legalNames.join(',') }),
          ...(apVendorKeys &&
            apVendorKeys.length > 0 && { apVendorKeys: apVendorKeys.join(',') })
        }
      }
    );
  }
}
